import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  DEFAULT_PALETTE,
  getPalette,
  isPaletteKey,
  type PaletteKey,
} from '@/lib/palettes';

// Theme runtime for the admin UI.
//
// Why an attribute rather than Tailwind's default media-query `dark:` variant:
// the app already shipped 13 `dark:` utilities while `index.css` defined only
// light tokens. Tailwind v4 wires `dark:` to `prefers-color-scheme` out of the
// box, so those utilities were firing for anyone on a dark-configured OS and
// painting dark amber panels inside an otherwise white app — including on the
// customer-facing /assist route. Binding the variant to `[data-theme="dark"]`
// (see the `@custom-variant` in index.css) means a dark utility can only apply
// when we've actually swapped the token block underneath it.
//
// 'system' is still the default, so the OS preference is honoured — it just
// goes through us instead of around us.

// ── Two independent axes ────────────────────────────────────────────────────
// `preference` (light/dark/system) and `palette` (Sepia, Nord, …) are separate
// on purpose: an agent who reads best on warm paper wants Sepia in daylight and
// its dark counterpart at night, without re-picking. So every palette ships both
// modes, and the two choices compose rather than replacing one another.

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

/** Shared with the inline boot script in index.html — keep the two in sync. */
export const THEME_STORAGE_KEY = 'pa-theme';
/** Ditto. */
export const PALETTE_STORAGE_KEY = 'pa-palette';

interface ThemeState {
  /** What the user picked. Persisted. */
  preference: ThemePreference;
  /** What's actually on screen after resolving 'system'. */
  resolved: ResolvedTheme;
  setPreference: (next: ThemePreference) => void;
  /** Colour palette, independent of light/dark. Persisted. */
  palette: PaletteKey;
  setPalette: (next: PaletteKey) => void;
}

const ThemeContext = createContext<ThemeState | undefined>(undefined);

function readStoredPreference(): ThemePreference {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    // Private mode / storage disabled — fall through to the system default.
  }
  return 'system';
}

function readStoredPalette(): PaletteKey {
  try {
    const raw = localStorage.getItem(PALETTE_STORAGE_KEY);
    if (isPaletteKey(raw)) return raw;
  } catch {
    // Private mode / storage disabled.
  }
  return DEFAULT_PALETTE;
}

function systemPrefersDark(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  );
}

function resolve(preference: ThemePreference): ResolvedTheme {
  if (preference === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return preference;
}

/** Keep the PWA theme-color meta on the palette's actual page background, so
 *  the mobile status bar and the installed-app title bar match the app instead
 *  of showing a hardcoded white/near-black seam. The hex comes from the same
 *  generated tokens the CSS uses, so it cannot drift. */
function syncThemeColor(palette: PaletteKey, mode: ResolvedTheme) {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', getPalette(palette).preview[mode].bg);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStoredPreference);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(readStoredPreference()));
  const [palette, setPaletteState] = useState<PaletteKey>(readStoredPalette);

  useEffect(() => {
    const next = resolve(preference);
    setResolved(next);
    const root = document.documentElement;
    root.setAttribute('data-theme', next);
    root.style.colorScheme = next;
    syncThemeColor(palette, next);
  }, [preference, palette]);

  useEffect(() => {
    document.documentElement.setAttribute('data-palette', palette);
  }, [palette]);

  // Follow the OS while the preference is 'system'. Without this, a user who
  // never touched the toggle would keep whatever theme was resolved at boot
  // even after macOS/Windows flipped at sunset.
  useEffect(() => {
    if (preference !== 'system') return;
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      const next: ResolvedTheme = mq.matches ? 'dark' : 'light';
      setResolved(next);
      document.documentElement.setAttribute('data-theme', next);
      document.documentElement.style.colorScheme = next;
      syncThemeColor(palette, next);
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [preference, palette]);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Best-effort; the in-memory preference still applies for this session.
    }
  }, []);

  const setPalette = useCallback((next: PaletteKey) => {
    setPaletteState(next);
    try {
      localStorage.setItem(PALETTE_STORAGE_KEY, next);
    } catch {
      // Best-effort.
    }
  }, []);

  const value = useMemo<ThemeState>(
    () => ({ preference, resolved, setPreference, palette, setPalette }),
    [preference, resolved, setPreference, palette, setPalette],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}
